import {
  sumOpeningDebtLines,
  type CashbookDebtCategory,
  type CashbookOpeningDebtLine
} from '@finvox/shared/cashbook';
import { parseMoneyInput, toMoney } from '@finvox/shared/money';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookOpeningDebtsDialog } from '@/components/cashbook/CashbookOpeningDebtsDialog';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { cn } from '@/lib/cn';

type Props = {
  amount: string;
  debtLines: CashbookOpeningDebtLine[];
  date: string;
  busy: boolean;
  mode?: 'create' | 'edit';
  saveDisabled?: boolean;
  saveDisabledReason?: string;
  debtOwners: string[];
  customDebtCategories?: CashbookDebtCategory[];
  onAmountChange: (v: string) => void;
  onDebtLinesChange: (lines: CashbookOpeningDebtLine[]) => void;
  onDateChange: (v: string) => void;
  onSave: () => void;
};

export function CashbookOpeningSetup({
  amount,
  debtLines,
  date,
  busy,
  mode = 'create',
  saveDisabled = false,
  saveDisabledReason,
  debtOwners,
  customDebtCategories = [],
  onAmountChange,
  onDebtLinesChange,
  onDateChange,
  onSave
}: Props) {
  const { t } = useTranslation();
  const [debtsDialogOpen, setDebtsDialogOpen] = useState(false);
  const isEdit = mode === 'edit';
  const amountValid = toMoney(parseMoneyInput(amount)).gt(0);
  const canSave = amountValid && !busy;
  const hint = isEdit ? t('cashbook.openingEditHint') : t('cashbook.openingHint');
  const saveLabel = isEdit ? t('cashbook.openingUpdate') : t('cashbook.openingSave');
  const saveBlockReason =
    saveDisabled && amountValid ? saveDisabledReason : undefined;
  const debtTotal = sumOpeningDebtLines(debtLines);

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold">
        {isEdit ? t('cashbook.openingEditTitle') : t('cashbook.openingTitle')}
      </h2>
      <p className="mb-3 mt-1 text-sm text-[var(--fv-text-muted)]">{hint}</p>
      <div
        className={cn(
          'flex flex-col gap-4 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4',
          'sm:flex-row sm:flex-wrap sm:items-end'
        )}
      >
        <div className="w-full sm:w-40">
          <label htmlFor="opening-amount" className="text-xs text-[var(--fv-text-muted)]">
            {t('cashbook.openingAmount')}
          </label>
          <MoneyInput
            id="opening-amount"
            className="mt-1 w-full"
            allowEmpty
            value={amount}
            onValueChange={onAmountChange}
            aria-label={t('cashbook.openingAmount')}
          />
        </div>
        <div className="w-full sm:w-56">
          <label className="text-xs text-[var(--fv-text-muted)]">
            {t('cashbook.debts.openingAmount')}
          </label>
          <Button
            type="button"
            variant="secondary"
            className="mt-1 w-full justify-between"
            onClick={() => setDebtsDialogOpen(true)}
            aria-label={t('cashbook.debts.openingLinesOpen')}
          >
            <span>
              {debtLines.length === 0
                ? t('cashbook.debts.openingLinesNone')
                : t('cashbook.debts.openingLinesCount', { count: debtLines.length })}
            </span>
            <span className="tabular-nums font-medium">
              <MoneyDisplay value={debtTotal} />
            </span>
          </Button>
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.openingDate')}</label>
          <DatePicker className="mt-1 w-full" value={date} onChange={onDateChange} />
        </div>
        <Button
          className="w-full sm:w-auto"
          fullWidth
          loading={busy}
          disabled={!canSave}
          title={
            !amountValid
              ? t('cashbook.openingAmountRequired')
              : saveBlockReason
          }
          onClick={onSave}
        >
          {saveLabel}
        </Button>
      </div>

      <CashbookOpeningDebtsDialog
        open={debtsDialogOpen}
        onClose={() => setDebtsDialogOpen(false)}
        lines={debtLines}
        debtOwners={debtOwners}
        customDebtCategories={customDebtCategories}
        onSave={onDebtLinesChange}
      />
    </div>
  );
}
